export type KkmServerCommandName =
  | 'CancelPaymentByPaymentCard'
  | 'CloseShift'
  | 'GetCounters'
  | 'GetDataCheck'
  | 'GetDataKKT'
  | 'GetRezult'
  | 'GetServerData'
  | 'List'
  | 'OpenShift'
  | 'PayByPaymentCard'
  | 'RegisterCheck'
  | 'ReturnPaymentByPaymentCard'
  | 'ValidationMarkingCode'
  | 'XReport';

/**
 * `5 (AlreadyDone)` — ответ на повтор `IdCommand`: команда есть в очереди KKMServer и не
 * закончилась ошибкой. Она могла ещё выполняться, поэтому это не подтверждение: исход
 * читается `GetRezult` (разбор kkmserver.dll 2.3.15.12 и 2.3.17.23).
 */
export type KkmServerCommandStatus = 0 | 1 | 2 | 3 | 4 | 5;

export type KkmServerCommand = Readonly<
  {
    Command: KkmServerCommandName | (string & {});
    IdCommand: string;
  } & Record<string, unknown>
>;

export interface KkmServerBridge {
  Execute(callback: (response: unknown) => void, command: KkmServerCommand): (() => void) | void;
}

export interface KkmServerResponse {
  command: string;
  error: string | null;
  idCommand: string;
  message: string | null;
  raw: Readonly<Record<string, unknown>>;
  status: KkmServerCommandStatus;
  warning: string | null;
}

export type KkmServerDeviceKind = 'acquiring' | 'kkt' | 'other';

export interface KkmServerDevice {
  active: boolean;
  idDevice: string;
  idTypeDevice: string | null;
  kind: KkmServerDeviceKind;
  name: string | null;
  numDevice: number;
  number: string | null;
  on: boolean;
}

export interface KkmServerInfo {
  licenseExpirationDate: string | null;
  serverVersion: string | null;
}

export interface KkmKktInfo {
  dateTime: string | null;
  ffdVersion: string | null;
  fiscal: boolean | null;
  fnDateEnd: string | null;
  fnNumber: string | null;
  fnMemoryOverflow: boolean | null;
  inn: string | null;
  kktNumber: string | null;
  licenseExpirationDate: string | null;
  model: string | null;
  ofdError: string | null;
  /** Название организации-владельца из регистрации ККТ; не все версии KKMServer его отдают. */
  organizationName: string | null;
  paperOver: boolean | null;
  /** Признак «торговля подакцизными товарами» из регистрации ККТ. */
  saleExcisableGoods: boolean | null;
  /** Признак «торговля маркированными товарами» из регистрации ККТ. */
  saleMarking: boolean | null;
  sessionNumber: number | null;
  sessionState: 1 | 2 | 3 | null;
  taxVariant: number | string | null;
}

export interface KkmAcquiringResult {
  authorizationCode: string | null;
  maskedPan: string | null;
  receiptNumber: string | null;
  rrn: string | null;
  universalId: string | null;
}

export interface KkmShiftCounters {
  cash: number | null;
  electronicPayment: number | null;
  total: number | null;
}

export class KkmProtocolValidationError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'KkmProtocolValidationError';
  }
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * `GetRezult` установленной версии отдаёт результат исходной команды внутри `Rezult`:
 * корневые `Status`/`IdCommand` описывают само чтение, а не команду (расширение
 * маршрутизирует такой ответ по `Rezult.IdCommand`). Прямой ответ команды возвращается как есть.
 */
export function unwrapKkmServerResult(value: unknown): unknown {
  return isRecord(value) && isRecord(value.Rezult) ? value.Rezult : value;
}

export function parseKkmServerResponse(value: unknown, expectedIdCommand: string): KkmServerResponse {
  if (!isRecord(value)) {
    throw new KkmProtocolValidationError('KKMServer вернул ответ неизвестного формата.');
  }

  const command = optionalString(value.Command);
  const idCommand = optionalString(value.IdCommand);
  const status = value.Status;

  if (command === null) {
    throw new KkmProtocolValidationError('В ответе KKMServer отсутствует Command.');
  }

  if (idCommand === null || idCommand !== expectedIdCommand) {
    throw new KkmProtocolValidationError('Ответ KKMServer не совпадает с correlation ID команды.');
  }

  if (!isKkmCommandStatus(status)) {
    throw new KkmProtocolValidationError('KKMServer вернул неизвестный статус команды.');
  }

  return {
    command,
    error: optionalString(value.Error),
    idCommand,
    message: optionalString(value.Message),
    raw: value,
    status,
    warning: optionalString(value.Warning),
  };
}

export function parseKkmServerInfo(response: KkmServerResponse): KkmServerInfo {
  const serverData = response.raw.ServerData;

  if (!isRecord(serverData)) {
    throw new KkmProtocolValidationError('GetServerData не содержит ServerData.');
  }

  return {
    licenseExpirationDate: optionalString(serverData.LicenseExpirationDate),
    serverVersion: optionalString(serverData.ServerVersion),
  };
}

export function parseKkmDevices(response: KkmServerResponse): KkmServerDevice[] {
  const list = response.raw.ListUnit;

  if (!Array.isArray(list)) {
    throw new KkmProtocolValidationError('List не содержит ListUnit.');
  }

  return list.map((value): KkmServerDevice => {
    if (!isRecord(value)) {
      throw new KkmProtocolValidationError('ListUnit содержит устройство неизвестного формата.');
    }

    const numDevice = value.NumDevice;
    const idDevice = optionalString(value.IdDevice);

    if (typeof numDevice !== 'number' || !Number.isInteger(numDevice) || numDevice < 0 || idDevice === null) {
      throw new KkmProtocolValidationError('ListUnit содержит устройство без стабильного identity.');
    }

    const idTypeDevice = optionalString(value.IdTypeDevice);

    return {
      active: value.Active === true,
      idDevice,
      idTypeDevice,
      kind: deviceKind(idTypeDevice),
      name: optionalString(value.NameDevice),
      numDevice,
      number: optionalString(value.NumberDevice) ?? optionalString(value.DeviceNumber),
      on: value.OnOff === true,
    };
  });
}

export function parseKkmKktInfo(response: KkmServerResponse): KkmKktInfo {
  const info = response.raw.Info;

  if (!isRecord(info)) {
    throw new KkmProtocolValidationError('GetDataKKT не содержит Info.');
  }

  const sessionState = info.SessionState;

  return {
    dateTime: optionalString(info.DateTimeKKT),
    ffdVersion: optionalString(info.FFDVersion),
    fiscal: optionalBoolean(info.FN_IsFiscal),
    fnDateEnd: optionalString(info.FN_DateEnd),
    fnNumber: optionalString(info.FnNumber) ?? optionalString(info.FN_Number),
    fnMemoryOverflow: optionalBoolean(info.FN_MemOverflowl),
    inn: optionalString(info.InnOrganization) ?? optionalString(info.INN),
    kktNumber: optionalString(info.KktNumber) ?? optionalString(info.KKTNumber),
    // Лицензия KKMServer привязана к ИНН ККТ и приходит здесь; `GetServerData.LicenseExpirationDate`
    // отдаёт заглушку `0001-01-02` даже при действующих лицензиях и о ККТ ничего не говорит.
    licenseExpirationDate: optionalString(info.LicenseExpirationDate),
    model: optionalString(info.Model) ?? optionalString(info.NameDevice),
    ofdError: optionalString(info.OFD_Error),
    organizationName: optionalString(info.NameOrganization),
    paperOver: optionalBoolean(info.PaperOver),
    // Признаки регистрации ККТ: подтверждены прод-журналом на 2.3.16.36–40.
    // Включаются перерегистрацией ККТ, а не настройкой KKMServer.
    saleExcisableGoods: optionalBoolean(info.SaleExcisableGoods),
    saleMarking: optionalBoolean(info.SaleMarking),
    // KkmServer 2.3.16.36 отдаёт `SessionNumber` в корне ответа, а не внутри `Info`
    // (подтверждено 951 записью прямого поллинга ККТ 21–22.08.2026: `Info.SessionNumber`
    // пуст всегда). `Info` остаётся fallback'ом на случай другой версии KkmServer.
    sessionNumber: optionalPositiveInteger(response.raw.SessionNumber) ?? optionalPositiveInteger(info.SessionNumber),
    sessionState: sessionState === 1 || sessionState === 2 || sessionState === 3 ? sessionState : null,
    taxVariant: typeof info.TaxVariant === 'string' || typeof info.TaxVariant === 'number' ? info.TaxVariant : null,
  };
}

/**
 * Номер смены из ответа самой команды `OpenShift`/`CloseShift`. KkmServer кладёт его в
 * корень рядом с `URL` и `QRCode` фискального документа — это прямое свидетельство того,
 * что касса приняла и провела документ. `GetDataKKT` обновляет `SessionState` позже
 * (21.08 — на минуты), поэтому ответ команды честнее повторного чтения состояния.
 */
export function parseKkmShiftSessionNumber(response: KkmServerResponse): number | null {
  return optionalPositiveInteger(response.raw.SessionNumber);
}

export function parseKkmAcquiringResult(response: KkmServerResponse): KkmAcquiringResult {
  const nested = isRecord(response.raw.RezultProcessing) ? response.raw.RezultProcessing : response.raw;

  return {
    authorizationCode: optionalString(nested.AuthorizationCode),
    maskedPan: optionalString(nested.CardNumber) ?? optionalString(nested.MaskedPan),
    receiptNumber: optionalString(nested.ReceiptNumber),
    rrn: optionalString(nested.RRNCode),
    universalId: optionalString(nested.UniversalID),
  };
}

export function parseKkmShiftCounters(response: KkmServerResponse): KkmShiftCounters {
  const rawCounters = response.raw['Сounters'] ?? response.raw.Counters;

  if (!Array.isArray(rawCounters)) {
    return emptyShiftCounters();
  }

  const shiftCounters = rawCounters.filter(
    (value): value is Record<string, unknown> =>
      isRecord(value) && (optionalString(value['СountersType']) ?? optionalString(value.CountersType)) === 'Shift',
  );

  const cash = netOrdersCounter(shiftCounters, 'Cash');
  const electronicPayment = netOrdersCounter(shiftCounters, 'ElectronicPayment');

  return {
    cash,
    electronicPayment,
    total: sumPresentCounters(cash, electronicPayment),
  };
}

export function vendorErrorCode(response: KkmServerResponse): string | null {
  return leadingNumericCode(response.error ?? response.message);
}

/** Код вендора KKMServer — ведущая числовая группа в тексте ошибки. */
export function leadingNumericCode(text: string | null | undefined): string | null {
  return text?.match(/^\s*([0-9]{1,8})\b/)?.[1] ?? null;
}

/** Имена полей KKMServer приходят в разном регистре и с разделителями. */
export function normalizeKkmPayloadKey(key: string): string {
  return key.replaceAll(/[^a-z0-9]/gi, '').toLocaleLowerCase('en-US');
}

export function isKkmCommandStatus(value: unknown): value is KkmServerCommandStatus {
  return value === 0 || value === 1 || value === 2 || value === 3 || value === 4 || value === 5;
}

/**
 * Устройство подтвердило выполнение команды: только `Ok` (0). Пара к
 * `KkmCommandStatus::confirmsExecution()` на backend: обе стороны контракта правятся вместе.
 */
export function kkmCommandStatusConfirmsExecution(status: KkmServerCommandStatus): boolean {
  return status === 0;
}

/**
 * Команда принята KKMServer, исход ещё не известен: `Run` (1) исполняется, `NotRun` (4) ждёт
 * очереди или готовности устройства, `AlreadyDone` (5) — повтор `IdCommand`, который уже
 * в очереди. Исход читается `GetRezult`; повторять команду нельзя.
 */
export function kkmCommandStatusIsPending(status: KkmServerCommandStatus): boolean {
  return status === 1 || status === 4 || status === 5;
}

function optionalBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** Приведение положительного целого из ответа KKMServer: числа приходят и строками. */
export function optionalPositiveInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string' && /^[1-9][0-9]*$/.test(value)) {
    return Number(value);
  }

  return null;
}

/** Приведение непустой строки из ответа KKMServer: пустые и пробельные значения — «нет данных». */
export function optionalString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();

  return normalized === '' ? null : normalized;
}

export function emptyShiftCounters(): KkmShiftCounters {
  return {
    cash: null,
    electronicPayment: null,
    total: null,
  };
}

function netOrdersCounter(counters: readonly Record<string, unknown>[], key: string): number | null {
  let found = false;
  let total = 0;

  for (const counter of counters) {
    const receiptType = optionalString(counter.ReceiptType)?.toLocaleLowerCase('en-US');

    if (
      receiptType !== 'shell' &&
      receiptType !== 'sell' &&
      receiptType !== 'shellreturn' &&
      receiptType !== 'sellreturn'
    ) {
      continue;
    }

    const value = optionalFiniteNumber(counter[key]);

    if (value === null) {
      continue;
    }

    found = true;
    total += receiptType.endsWith('return') ? -value : value;
  }

  return found ? total : null;
}

/** Число из ответа KKMServer: приходит и числом, и строкой с запятой. */
export function optionalFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsed = Number(value.replace(',', '.'));

  return Number.isFinite(parsed) ? parsed : null;
}

function sumPresentCounters(...values: Array<number | null>): number | null {
  const present = values.filter((value): value is number => value !== null);

  return present.length === 0 ? null : present.reduce((sum, value) => sum + value, 0);
}

function deviceKind(idTypeDevice: string | null): KkmServerDeviceKind {
  if (idTypeDevice === 'GateSbRf') {
    return 'acquiring';
  }

  if (idTypeDevice?.startsWith('Kkm') === true) {
    return 'kkt';
  }

  return 'other';
}
